// Used in login-routing.module.ts

// Angular
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatStepper } from "@angular/material/stepper";
import { Router } from "@angular/router";

// Services/Types/Helpers
import { StatesHelper } from "#core/helpers/states.helper";
import { TimezoneHelper } from "#core/helpers/timezone.helper";
import { CookieService } from "#core/services/cookie.service";
import { FormHelper } from "#helpers/form.helper";
import { SnackBarService } from "#services/snackbar.service";
import { PPUser as User } from "cli-types";

// Validators
import { EmailValidator } from "#validators/email.validator";
import { ProfileValidator, PwdLength } from "#validators/profile.validator";

// RXJS/Store
import { select, Store } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import * as fromStore from "../../store";

// Moment Timezone
import { Auth, FidoService, OnboardNewStudentRequest, OnboardNewTeacherRequest, ProfileService, SchoolsFidoService, UsersService } from "@carnegie-learning/data-access-angular-passport";
import * as momentTz from "moment-timezone";

@Component({
    selector: "app-login-onboard",
    templateUrl: "./login-onboard-new-user.component.html",
    styleUrls: ["./login-onboard-new-user.component.scss"],
    standalone: false
})
export class LoginOnboardNewUserComponent implements OnInit, OnDestroy {
    showThumbnail = false;
    loginFlag = false;
    profileForm?: UntypedFormGroup;
    newUserEmail = "";
    inviteHash = "";
    progressBar$?: Observable<boolean>;
    bannerStatus$?: Observable<boolean>;
    bannerMessage$?: Observable<string>;
    bannerColor$?: Observable<string>;
    private ngUnsubscribe = new Subject();

    // Set this after the user profile is created.
    profileData$?: Observable<User>;

    usStates = this.statesHelper.getStates();
    timezones = this.timezoneHelper.getTimezones();

    constructor(
        public auth: Auth,
        private fb: UntypedFormBuilder,
        public formHelper: FormHelper,
        public store: Store<fromStore.UserState>,
        private router: Router,
        private profileService: ProfileService,
        private timezoneHelper: TimezoneHelper,
        private statesHelper: StatesHelper,
        private usersService: UsersService,
        private fidoService: FidoService,
        private snackBarService: SnackBarService,
        private cookieService: CookieService,
        private schoolsFidoService: SchoolsFidoService
    ) {}

    ngOnInit() {
        this.profileForm = this.fb.group({
            firstName: [
                "",
                {
                    validators: [
                        Validators.required,
                        Validators.maxLength(255),
                    ],
                    updateOn: "change",
                },
            ],
            lastName: [
                "",
                {
                    validators: [
                        Validators.required,
                        Validators.minLength(2),
                        Validators.maxLength(255),
                    ],
                    updateOn: "change",
                },
            ],
            timezone: ["", [Validators.required, Validators.maxLength(255)]],
            state: ["", [Validators.required, Validators.maxLength(255)]],
            email: [
                { value: "", disabled: true },
                {
                    validators: [Validators.required, EmailValidator.email],
                    updateOn: "change",
                },
            ],
            inviteHash: [
                { value: "", disabled: true },
                {
                    validators: [Validators.required],
                    updateOn: "change",
                },
            ],
            passwordGroup: this.fb.group(
                {
                    newPassword: [
                        "",
                        { validators: [Validators.required, PwdLength()] },
                    ],
                    confirmPassword: [
                        "",
                        { validators: [Validators.required] },
                    ],
                },
                {
                    validator: ProfileValidator.matchPassword,
                }
            ),
        });

        this.store
            .pipe(select(fromStore.getAuthorized))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data === false) {
                    // you can only access this page through the register form
                    this.store.dispatch(fromStore.registerAuthFail());
                    this.router.navigate(["login/register"]);
                } else {
                    // user was redirected and entered a valid email address
                    this.progressBar$ = this.store.pipe(
                        select(fromStore.getRegisterProgressBar)
                    );
                    this.bannerStatus$ = this.store.pipe(
                        select(fromStore.getRegisterBannerStatus)
                    );
                    this.bannerMessage$ = this.store.pipe(
                        select(fromStore.getRegisterBannerMessage)
                    );
                    this.bannerColor$ = this.store.pipe(
                        select(fromStore.getRegisterBannerColor)
                    );

                    this.store
                        .pipe(select(fromStore.getEmail))
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((email) => {
                            this.newUserEmail = email;
                            this.profileForm?.get("email")?.setValue(email);
                        });

                    this.store
                        .pipe(select(fromStore.getInviteHash))
                        .pipe(takeUntil(this.ngUnsubscribe))
                        .subscribe((inviteHash) => {
                            this.inviteHash = inviteHash;
                            const prefix = this.inviteHash.substring(0, 1);
                            let inviteType = "";
                            if (prefix === "s") {
                                inviteType = "schoolInviteHash=";
                            } else if (prefix === "c") {
                                inviteType = "courseInviteHash=";
                                this.profileForm?.get("state")?.disable();
                                this.profileForm?.get("timezone")?.disable();
                            }

                            this.usersService
                                .getObjFromInviteCode(
                                    this.inviteHash,
                                    inviteType
                                )
                                .pipe(takeUntil(this.ngUnsubscribe))
                                .subscribe(
                                    (response) => {
                                        let tz = response.timezone;
                                        if (tz === null || tz === "GMT") {
                                            tz = momentTz.tz.guess();
                                        }
                                        this.profileForm?.patchValue({
                                            timezone: tz,
                                            state: response.state,
                                        });
                                    },
                                    (error: HttpErrorResponse) => {
                                        // console.log(error.status);
                                    }
                                );

                            this.profileForm
                                ?.get("inviteHash")
                                ?.setValue(inviteHash);
                        });
                }
            });

        this.store.dispatch(fromStore.registerStep1());
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    // error validator for profileForm
    validate(field: string, type: string) {
        return this.profileForm
            ? this.formHelper.Validate(this.profileForm, field, type)
            : false;
    }

    // password validation chip color
    chipColor(type: string) {
        if (!this.profileForm?.get("passwordGroup.newPassword")?.touched) {
            // initial state
            return "info";
        }

        const result = this.formHelper.Validate(
            this.profileForm,
            "passwordGroup.newPassword",
            type
        );
        return result ? "info" : "primary";
    }

    // Dispatches corresponding actions for profile form submission
    createAccount() {
        const prefix = this.inviteHash.substring(0, 2);
        switch (prefix) {
            case "c-": // add student to teacher-class
                const studentPayload: OnboardNewStudentRequest = {
                    courseInviteHash:
                        this.profileForm?.get("inviteHash")?.value,
                    eAddress: this.profileForm?.get("email")?.value,
                    firstName: this.profileForm?.get("firstName")?.value,
                    lastName: this.profileForm?.get("lastName")?.value,
                    timezone: this.profileForm?.get("timezone")?.value,
                    password: this.profileForm?.get("passwordGroup.newPassword")
                        ?.value,
                };
                this.store.dispatch(
                    fromStore.registerFormAttemptStudent({
                        payload: studentPayload,
                    })
                );
                break;
            default: // add teacher to school
                let teacherPayload: OnboardNewTeacherRequest = {
                    schoolInviteHash:
                        this.profileForm?.get("inviteHash")?.value,
                    eAddress: this.profileForm?.get("email")?.value,
                    firstName: this.profileForm?.get("firstName")?.value,
                    lastName: this.profileForm?.get("lastName")?.value,
                    timezone: this.profileForm?.get("timezone")?.value,
                    password: this.profileForm?.get("passwordGroup.newPassword")
                        ?.value,
                };
                if (this.profileForm?.get("state")?.value) {
                    teacherPayload = {
                        ...teacherPayload,
                        state: this.profileForm?.get("state")?.value,
                    };
                }
                this.store.dispatch(
                    fromStore.registerFormAttemptTeacher({
                        payload: teacherPayload,
                    })
                );
                break;
        }
    }

    step1Next(stepper: MatStepper) {
        this.profileForm?.markAsUntouched();
        this.createAccount();
        this.store
            .pipe(select(fromStore.getStep))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((value) => {
                if (value === 2) {
                    this.profileData$ = this.store.pipe(
                        select(fromStore.getUserProfile)
                    );
                    this.showThumbnail = true; // keep true to add profile
                    this.login()
                    stepper.next();
                }
            });
    }

    login() {
        this.loginFlag = true;
        const username = this.profileForm?.get("email")?.value;
        const pwd = this.profileForm?.get("passwordGroup.newPassword")?.value;

        this.fidoService
            .portalLogin(
                "loginId=" +
                    encodeURIComponent(username) +
                    "&password=" +
                    encodeURIComponent(pwd)
            )
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
                (resp: any) => {
                    this.snackBarService.success(
                        "Account Created!"
                    );
                },
                (error: any) => {
                    this.snackBarService.error(
                        "Login Failed, Please Try Again Via MyCL!"
                    );
                    this.store.dispatch(fromStore.registerAuthFail());
                }
            );
    }

    continue() {
        this.enforceVersion();
        this.store.dispatch(fromStore.registerAutoLogin());
    }

    cancel() {
        this.store.dispatch(fromStore.registerCancel());
        this.router.navigate(["login/register"]);
    }

    // PAS-11440 enforce versioning for schools
    // TODO delete this when PAS-11167 is completed
    enforceVersion() {
        const fidoSchoolID = this.cookieService.get("CL_SCHOOL");
        if (fidoSchoolID) {
            this.schoolsFidoService
                .getSchool(fidoSchoolID)
                .pipe(first())
                .subscribe(
                    (schoolData) => {
                        const defaultURL = (<any>window).passportHost;
                        let versionedURL = null;

                        if (schoolData && schoolData.vers) {
                            schoolData.vers.forEach((application) => {
                                if (application.includes("-passport")) {
                                    const schoolVersion = application.replace(
                                        "-passport",
                                        ""
                                    );
                                    versionedURL =
                                        defaultURL + "/" + schoolVersion;
                                }
                            });
                        }
                        if (versionedURL !== null) {
                            // version found
                            (<any>window).location.href =
                                versionedURL + "/#/login/form";
                        } else {
                            this.router.navigate(["login/form"]);
                        }
                    },
                    (error) => {
                        // if no school id is found, use the current URL version
                        this.router.navigate(["login/form"]);
                    }
                );
        }
    }
}
